import type { FlowState } from '~/flow/types';
import type { FeatureFlags } from '~/helpers/typescript';
import { transformLegacyRepresentationToJson, keyServicesObjectByInternalName } from '../../engine/transformation';
import { transformServices } from '../../engine/transformation/workflowProps';
import { findAmbiguousRuleErrors } from 'flow/store/engine/validation/ambiguousRules';
import type { WorkflowPropsResponse, WorkflowFailureHandlingMode, WorkflowProps } from '../../engine/types';
import { initialState } from '../state';
import type { SdkConfigPayload } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';

function createWorkflowInfo(workflowData: FlowState, workflowProps: WorkflowPropsResponse) {
  return workflowData.version?.application_token
    ? {
      name: workflowData.version.application_name,
      token: workflowData.version.application_token,
      publicVersion: workflowData.version.public_version,
    }
    : {
      name: workflowProps.workflow.name,
      token: workflowProps.workflow.application_token,
      publicVersion: null,
    };
}

// TODO(BUILD-1869): transform props
function createInitialWorkflowProps(
  workflowProps: WorkflowPropsResponse,
  publishedAttributes: WorkflowProps['publishedAttributes'],
): WorkflowProps {
  return {
    allWorkflows: workflowProps.allWorkflows,
    importableAttributes: workflowProps.importableAttributes,
    lists: workflowProps.lists,
    customLists: workflowProps.customLists,
    outcomes: workflowProps.outcomes,
    publishedAttributes,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
    reasonCodes: workflowProps.reasonCodes,
    recurringWorkflows: workflowProps.recurringWorkflows,
    // todo BUILD-2731 - remove the outer transformation call
    services: transformServices(
      keyServicesObjectByInternalName(workflowProps.services),
    ),
    tags: workflowProps.tags,
    tagTypes: workflowProps.tagTypes,
    workflow: workflowProps.workflow,
    sandboxPersonas: workflowProps.sandboxPersonas,
    customers: workflowProps.customers,
    categories: workflowProps.categories,
  };
}

function createInitialWorkflowData(
  transformationResult: any,
  sdkConfig: SdkConfigPayload,
  workflowInfo: ReturnType<typeof createWorkflowInfo>,
  workflowData: FlowState,
) {
  return {
    ...transformationResult,
    ...workflowInfo,
    sdkConfig,
    workflowsOutcomeReasonTagsByTagId: workflowData.workflowsOutcomeReasonTagsByTagId,
    failureHandlingMode: (workflowData.version.use_best_effort_threshold_rule_parser
      ? 'best-effort'
      : 'full-threshold') as WorkflowFailureHandlingMode,
  };
}

function createFeatureFlagsForStore(featureFlags: Partial<FeatureFlags>) {
  return {
    allowBestEffortRuleEvaluations: featureFlags.allowBestEffortRuleEvaluations,
    collapseInputAttributes: featureFlags.collapseInputAttributes,
    customTransactionAttributes: featureFlags.customTransactionAttributes,
    ruleBuilderRefactor: featureFlags.ruleBuilderRefactor,
    sortWorkflowNodesByType: featureFlags.sortWorkflowNodesByType,
    useTyphonWorkflowEndpoints: featureFlags.useTyphonWorkflowEndpoints,
  };
}

export function transformWorkflowInitializationData({
  workflowData,
  workflowProps,
  featureFlags,
}: {
  workflowData: FlowState;
  workflowProps: WorkflowPropsResponse;
  featureFlags: Partial<FeatureFlags>;
}) {
  // Transform published attributes once, before passing to both transformation and props
  const publishedAttributesWithEphemeral = workflowProps.publishedAttributes.map(pa => ({
    ...pa,
    isEphemeral: false,
  }));

  // Transform legacy representation data as part of initialization
  const transformationResult = transformLegacyRepresentationToJson({
    actions: workflowData.actions,
    actionOptions: workflowData.actionOptions,
    thresholds: workflowData.thresholds,
    dataRoutes: workflowData.dataRoutes,
    tags: workflowProps.tags,
    services: workflowProps.services,
    outcomes: workflowProps.outcomes,
    publishedAttributes: publishedAttributesWithEphemeral,
    matrixModels: workflowProps.matrixModels,
    lists: workflowProps.lists,
    customLists: workflowProps.customLists,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
  });

  const workflowInfo = createWorkflowInfo(workflowData, workflowProps);
  const initWorkflowProps = createInitialWorkflowProps(
    workflowProps,
    publishedAttributesWithEphemeral,
  );

  const { legacyMappings, ...restTransformationResult } = transformationResult;
  const initWorkflowData = createInitialWorkflowData(
    restTransformationResult,
    workflowData.sdkConfig,
    workflowInfo,
    workflowData,
  );

  const ambiguousRuleErrors = findAmbiguousRuleErrors({
    legacyActions: workflowData.actions,
    legacyToTransformedThresholdIdMappings: legacyMappings.thresholds,
  });

  const featureFlagsForStore = createFeatureFlagsForStore(featureFlags);

  return {
    initWorkflowProps,
    initWorkflowData,
    legacyMappings,
    ambiguousRuleErrors,
    initWorkflowFeatures: initialState.workflowFeatures,
    featureFlagsForStore,
  };
}
